import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';
import { getSortedCountryNames } from '../../constants/countries';

/**
 * PersonalInfo component for collecting user's personal information
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const PersonalInfo = ({
  stepNumber = 3,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = "",
  description = "",
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();
  const formRef = useRef(null);
  const [hasScrolledToErrors, setHasScrolledToErrors] = useState(false);

  // Use translations with fallbacks
  const stepTitle = title || t('wizard.personal.title', 'Personal Information');
  const stepDescription = description || t('wizard.personal.description', 'Tell us about yourself so we can provide personalized immigration guidance.');
  const [formData, setFormData] = useState({
    firstName: state.personalInfo.firstName || '',
    lastName: state.personalInfo.lastName || '',
    email: state.personalInfo.email || '',
    phone: state.personalInfo.phone || '',
    dateOfBirth: state.personalInfo.dateOfBirth || '',
    nationality: state.personalInfo.nationality || '',
    currentCountry: state.personalInfo.currentCountry || ''
  });

  const [errors, setErrors] = useState({});

  // Use centralized country list with South Asian countries prioritized
  const countries = useMemo(() => getSortedCountryNames(), []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('wizard.personal.errors.firstName.required', 'First name is required');
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = t('wizard.personal.errors.firstName.min', 'First name must be at least {{min}} characters', { min: 2 });
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('wizard.personal.errors.lastName.required', 'Last name is required');
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = t('wizard.personal.errors.lastName.min', 'Last name must be at least {{min}} characters', { min: 2 });
    }

    if (!formData.email.trim()) {
      newErrors.email = t('wizard.personal.errors.email.required', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('wizard.personal.errors.email.invalid', 'Please enter a valid email address');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('wizard.personal.errors.phone.required', 'Phone number is required');
    } else if (!/^[+]?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = t('wizard.personal.errors.phone.invalid', 'Please enter a valid phone number');
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = t('wizard.personal.errors.dateOfBirth.required', 'Date of birth is required');
    } else {
      const age = calculateAge(formData.dateOfBirth);
      if (age < 16 || age > 65) {
        newErrors.dateOfBirth = t('wizard.personal.errors.dateOfBirth.range', 'Age must be between {{min}} and {{max}} years', { min: 16, max: 65 });
      }
    }

    if (!formData.nationality) {
      newErrors.nationality = t('wizard.personal.errors.nationality.required', 'Nationality is required');
    }

    if (!formData.currentCountry) {
      newErrors.currentCountry = t('wizard.personal.errors.currentCountry.required', 'Current country is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const calculateAge = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handleNext = useCallback(() => {
    setHasScrolledToErrors(false);
    if (validateForm()) {
      // Update wizard state with personal info
      actions.updatePersonalInfo(formData);

      if (onNext) {
        onNext({
          personalInfo: formData
        });
      }
    } else if (formRef.current && !hasScrolledToErrors) {
      // Auto-scroll to first error with smooth animation
      setTimeout(() => {
        const firstErrorField = Object.keys(errors)[0];
        const errorElement = formRef.current.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          setHasScrolledToErrors(true);
        }
      }, 100);
    }
  }, [formData, validateForm, actions, onNext, errors, hasScrolledToErrors]);

  // Auto-scroll to first error when errors are shown
  useEffect(() => {
    if (Object.keys(errors).length > 0 && formRef.current && !hasScrolledToErrors) {
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = formRef.current.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.focus({
          preventScroll: true
        });
        setTimeout(() => {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          setHasScrolledToErrors(true);
        }, 100);
      }
    }
  }, [errors, hasScrolledToErrors]);

  const canProceed = useMemo(() => {
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'nationality', 'currentCountry'];
    return requiredFields.every(field => formData[field] && formData[field].trim()) &&
      Object.keys(errors).every(field => !errors[field]);
  }, [formData, errors]);

  // Validate on mount to show any initial errors
  React.useEffect(() => {
    if (Object.keys(formData).some(key => formData[key])) {
      validateForm();
    }
  }, []);

  return (
    <div className={`personal-info-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Form */}
      <div ref={formRef} className="card p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.firstName.label', 'First Name *')}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.personal.fields.firstName.placeholder', 'Enter your first name')}
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.lastName.label', 'Last Name *')}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.personal.fields.lastName.placeholder', 'Enter your last name')}
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.email.label', 'Email Address *')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.personal.fields.email.placeholder', 'your.email@example.com')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.phone.label', 'Phone Number *')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.personal.fields.phone.placeholder', '+94 77 123 4567')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('wizard.personal.phoneHelper', 'Include your country code so we can reach you on WhatsApp or voice call without delays.')}
            </p>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.dateOfBirth.label', 'Date of Birth *')}
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('wizard.personal.fields.dateOfBirth.helper', 'We use this to score age-based visa points and set reminders before eligibility thresholds.')}
            </p>
            {errors.dateOfBirth && (
              <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
            )}
          </div>

          {/* Nationality */}
          <div>
            <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.nationality.label', 'Nationality *')}
            </label>
            <select
              id="nationality"
              name="nationality"
              value={formData.nationality}
              onChange={(e) => handleInputChange('nationality', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.nationality ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.personal.fields.nationality.placeholder', 'Select your nationality')}</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.nationality && (
              <p className="mt-1 text-sm text-red-600">{errors.nationality}</p>
            )}
          </div>

          {/* Current Country */}
          <div className="md:col-span-2">
            <label htmlFor="currentCountry" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.personal.fields.currentCountry.label', 'Current Country of Residence *')}
            </label>
            <select
              id="currentCountry"
              name="currentCountry"
              value={formData.currentCountry}
              onChange={(e) => handleInputChange('currentCountry', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.currentCountry ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.personal.fields.currentCountry.placeholder', 'Select your current country')}</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.currentCountry && (
              <p className="mt-1 text-sm text-red-600">{errors.currentCountry}</p>
            )}
          </div>
        </div>

      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.next', 'Next Step')}
        </button>
      </div>
    </div>
  );
};

export default PersonalInfo;
